import CreditCardIcon from 'assets/images/icons/CreditCardIcon.svg?react';
import { CallForActionButton, CallForActionButtonTooltip } from 'components/buttons/CallForActionButton';
import Loader from 'components/Loader';
import ExpenseModal from 'components/modals/expenses/ExpenseModal';
import { useModal } from 'components/modals/useModal';
import useAuth from 'hooks/useAuth.ts';
import { useCallback, useMemo } from 'react';
import { useGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useAddExpenseMutation } from 'store/api/expenses';
import { EmployeeListItemMinDto } from 'types/employees';
import { ExpenseCreateDto, ExpenseForm } from 'types/expenses';
import { TimecardListItemData } from 'types/timecards';
import { errorAlert } from 'utils/alert';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { useExpenseEntryDisabled } from 'utils/useExpenseEntryDisabled';

type Props = {
    timecard: TimecardListItemData;
};

const AddExpenseIconButton = ({ timecard: timecardData }: Props): JSX.Element | null => {
    const { user } = useAuth();
    const userId = user?.id;
    const isExpenseEntryDisabled = useExpenseEntryDisabled();

    const { openModal, closeModal } = useModal();

    const [addExpense] = useAddExpenseMutation();
    const { isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['CURRENCY', 'EXPENSE_TYPE'],
    });

    const { data: currentEmployeeQueryData, isLoading: isLoadingCurrentEmployeeQueryData } =
        useGetEmployeesListWithFiltersQuery({ pageSize: 1, page: 0, filters: `id==${userId}` }, { skip: !userId });

    const currentEmployeeData = useMemo<EmployeeListItemMinDto | undefined>(() => {
        return currentEmployeeQueryData?.content.at(0);
    }, [currentEmployeeQueryData]);

    const handleExpenseCreate = useCallback(
        async (expenseDto: ExpenseCreateDto) => {
            try {
                await addExpense(expenseDto);
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [addExpense]
    );

    const timecardId = timecardData?.id ?? null;
    const matterId = timecardData?.matter.id;

    const openExpenseModal = useCallback((): void => {
        const preSelectedEmployee = currentEmployeeData;

        const initalData: ExpenseForm = {
            externalId: null,
            expenseType: null,
            currencyType: null,
            description: '',
            expenseAmount: '',
            expenseDate: formatDateStringInput(new Date()),
            isNonBillable: false,
            employee: preSelectedEmployee ? { id: preSelectedEmployee?.id, name: preSelectedEmployee?.name } : null,
            matter: null, // prefilled at ExpenseModal component level
            effortId: null,
            timecardId,
        };

        openModal(
            <ExpenseModal
                onChange={(value) => {
                    void handleExpenseCreate(value);
                }}
                initialData={initalData}
                onClose={closeModal}
                titleId={'expenses.add'}
                disableSelectMatter={true}
                readOnly={false}
                preselectMatterId={matterId ?? undefined}
            />,
            closeModal
        );
    }, [currentEmployeeData, closeModal, handleExpenseCreate, openModal, timecardId, matterId]);

    if (isLoadingClassifiers || isLoadingCurrentEmployeeQueryData) {
        return <Loader />;
    }

    return (
        <CallForActionButtonTooltip
            title={isExpenseEntryDisabled ? t('expenses.integration-entry-disabled') : t('timecards.add-expense')}
        >
            <span>
                <CallForActionButton
                    variant="contained"
                    shape="rounded"
                    color="info"
                    aria-label={t('timecards.add-expense')}
                    disabled={isExpenseEntryDisabled}
                    onClick={() => openExpenseModal()}
                >
                    <CreditCardIcon />
                </CallForActionButton>
            </span>
        </CallForActionButtonTooltip>
    );
};

export default AddExpenseIconButton;
